import { keepPreviousData } from '@tanstack/react-query';
import { useListCandidates } from '@sara/api-client';
import type { SortingState } from '@tanstack/react-table';
import { Skeleton } from '@sara/ui/components/skeleton';
import { CANDIDATE_SORT_FIELDS, type CandidateSortField } from '../../../../router/tree';
import { CandidateListToolbar } from '../../components/CandidateListToolbar';
import { CandidateListTable, CandidateListPagination } from '../../components/CandidateListTable';
import type { CandidateListContainerProps } from './types';

function isSortField(id: string): id is CandidateSortField {
  return (CANDIDATE_SORT_FIELDS as readonly string[]).includes(id);
}

export function CandidateListContainerContent({
  orgId,
  search,
  onSearchChange,
  onRowClick,
}: CandidateListContainerProps) {
  const { data, isLoading, error, isFetching } = useListCandidates(
    {
      orgId,
      search: search.search,
      sortBy: search.sortBy,
      sortOrder: search.sortOrder,
      page: search.page,
      pageSize: search.pageSize,
    },
    { query: { placeholderData: keepPreviousData } }
  );

  if (error) throw error;

  if (isLoading) {
    return (
      <div data-testid="candidate-list-loading" className="flex min-h-0 flex-1 flex-col gap-6">
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-9 w-64 rounded-full" />
        </div>
        <div className="space-y-px border-y border-border/70">
          <Skeleton className="h-16 rounded-none" />
          <Skeleton className="h-16 rounded-none" />
          <Skeleton className="h-16 rounded-none" />
        </div>
      </div>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const sorting: SortingState = [{ id: search.sortBy, desc: search.sortOrder === 'desc' }];

  function handleSortingChange(updater: SortingState | ((prev: SortingState) => SortingState)) {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    if (next.length === 0) return;
    const { id, desc } = next[0];
    if (!isSortField(id)) {
      throw new Error(`Attempted to sort by non-sortable column '${id}'`);
    }
    onSearchChange({ sortBy: id, sortOrder: desc ? 'desc' : 'asc', page: 1 });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <CandidateListToolbar
        value={{ search: search.search }}
        onChange={(next) => onSearchChange({ ...next, page: 1 })}
      />

      <CandidateListTable
        items={items}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        onRowClick={onRowClick}
        isFetching={isFetching}
        searchTerm={search.search}
        footer={
          total > 0 ? (
            <CandidateListPagination
              page={search.page}
              pageSize={search.pageSize}
              total={total}
              onPageChange={(page) => onSearchChange({ page })}
              onPageSizeChange={(pageSize) => onSearchChange({ pageSize, page: 1 })}
            />
          ) : undefined
        }
      />
    </div>
  );
}
